import React, { useEffect, useState, useCallback } from "react";
import { Card, Button, Modal, Form } from "react-bootstrap";
import { Link, useParams } from "react-router-dom";
import axiosInstance from "../services/axiosInterceptor";
import { getUserId } from "../services/authorize";
import { useLoading } from "../contexts/LoadingContext";
import { useLanguage } from "../contexts/LanguageContext";
import th   from "../locales/islamic/th";
import jawi from "../locales/islamic/jawi";
import Swal from "sweetalert2";

export default function IslamicScoreEntry() {
  const { is_teachId } = useParams();
  const { setLoading } = useLoading();
  const { lang } = useLanguage();
  const t = lang === 'th' ? th : jawi;

  const [teaching, setTeaching] = useState(null);
  const [students, setStudents] = useState([]);
  const [scores, setScores]     = useState({});

  const [showScoreModal, setShowScoreModal] = useState(false);
  const [modalStudent, setModalStudent]     = useState(null);
  const [modalTerm1, setModalTerm1]         = useState("");
  const [modalTerm2, setModalTerm2]         = useState("");
  const [modalSaving, setModalSaving]       = useState(false);

  const handleOpenScoreModal = (student) => {
    const current = scores[student.is_study_id] || { term1: "", term2: "" };
    setModalStudent(student);
    setModalTerm1(current.term1);
    setModalTerm2(current.term2);
    setShowScoreModal(true);
  };

  const validateScore = (val) => val === "" || (/^\d*\.?\d*$/.test(val) && Number(val) <= 50);

  const handleModalTerm1 = (val) => { if (validateScore(val)) setModalTerm1(val); };
  const handleModalTerm2 = (val) => { if (validateScore(val)) setModalTerm2(val); };

  const handleModalSave = async () => {
    const t_id = getUserId();
    setModalSaving(true);
    try {
      await axiosInstance.post(`${process.env.REACT_APP_API}/islamic-teaching/saveScore`, {
        is_teachId: Number(is_teachId),
        t_id:       Number(t_id),
        scores: [{
          is_study_id:  modalStudent.is_study_id,
          isubject_id:  teaching?.isubject_id || null,
          is_scoreVal1: modalTerm1 === "" ? null : Number(modalTerm1),
          is_scoreVal2: modalTerm2 === "" ? null : Number(modalTerm2),
        }],
      });
      setScores(prev => ({
        ...prev,
        [modalStudent.is_study_id]: { term1: modalTerm1, term2: modalTerm2 },
      }));
      setShowScoreModal(false);
      Swal.fire({ icon: "success", title: t.scoreEntry.saveSuccess, timer: 1200, showConfirmButton: false });
    } catch {
      Swal.fire({ icon: "error", title: t.scoreEntry.saveError });
    } finally {
      setModalSaving(false);
    }
  };

  const fetchData = useCallback(() => {
    setLoading(true);
    axiosInstance
      .get(`${process.env.REACT_APP_API}/islamic-teaching/studentsForScore/${is_teachId}`)
      .then(res => {
        setTeaching(res.data.teaching);
        setStudents(res.data.students);
        const initial = {};
        res.data.students.forEach(s => {
          initial[s.is_study_id] = {
            term1: s.is_scoreVal1 != null ? String(s.is_scoreVal1) : (s.is_scoreVal != null ? String(s.is_scoreVal) : ""),
            term2: s.is_scoreVal2 != null ? String(s.is_scoreVal2) : "",
          };
        });
        setScores(initial);
      })
      .catch(() => {
        setTeaching(null);
        setStudents([]);
      })
      .finally(() => setLoading(false));
  }, [is_teachId, setLoading]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const average = () => {
    const vals = [];
    Object.values(scores).forEach(row => {
      if (!row) return;
      if (row.term1 !== "") vals.push(Number(row.term1));
      if (row.term2 !== "") vals.push(Number(row.term2));
    });
    if (vals.length === 0) return "-";
    return (vals.reduce((a, b) => a + b, 0) / vals.length).toFixed(2);
  };

  return (
    <React.Fragment>
      <div className="d-flex align-items-center mb-3">
        <Link to="/islamic/my-subjects" className="btn btn-sm btn-outline-secondary me-3">
          <i className="ri-arrow-left-line"></i>
        </Link>
        <div>
          <h4 className="main-title mb-0">{t.scoreEntry.pageTitle}</h4>
          {teaching && (
            <p className="text-secondary mb-0 arabic-text">
              {teaching.isubject_isid1} — {teaching.isubject_name}
            </p>
          )}
        </div>
      </div>

      <Card className="card-one">
        <Card.Header className="d-flex align-items-center">
          <span className="fw-semibold">{t.scoreEntry.studentCount(students.length)}</span>
          <span className="ms-auto text-secondary small">{t.scoreEntry.average}: {average()}</span>
        </Card.Header>
        <Card.Body className="p-0">
          {students.length === 0 ? (
            <p className="text-center text-secondary py-4">{t.scoreEntry.noStudents}</p>
          ) : (
            <div className="table-responsive">
            <table className="table table-hover mb-0 text-nowrap">
              <thead className="table-secondary">
                <tr>
                  <th style={{ width: 40 }}>#</th>
                  <th>{t.scoreEntry.colId}</th>
                  <th>{t.scoreEntry.colArabicName}</th>
                  <th>{t.scoreEntry.colName}</th>
                  <th style={{ width: 90 }} className="text-center">{t.scoreEntry.semester1}</th>
                  <th style={{ width: 90 }} className="text-center">{t.scoreEntry.semester2}</th>
                  <th style={{ width: 80 }} className="text-center">{t.scoreEntry.total}</th>
                  <th style={{ width: 80 }}></th>
                </tr>
              </thead>
              <tbody>
                {students.map((s, idx) => {
                  const term1 = scores[s.is_study_id]?.term1 ?? "";
                  const term2 = scores[s.is_study_id]?.term2 ?? "";
                  const nums = [term1, term2].map(v => v === "" ? null : Number(v)).filter(v => v != null);
                  const sumValue = nums.length === 0 ? "—" : nums.reduce((a, b) => a + b, 0);
                  const hasScore = term1 !== "" || term2 !== "";
                  return (
                    <tr key={s.is_study_id}>
                      <td className="align-middle text-secondary">{idx + 1}</td>
                      <td className="align-middle">{s.st_idnumber}</td>
                      <td className="align-middle arabic-text">{s.st_arabicname || <span className="text-muted">—</span>}</td>
                      <td className="align-middle">{s.st_name}</td>
                      <td className="align-middle text-center">{term1 !== "" ? term1 : <span className="text-muted">—</span>}</td>
                      <td className="align-middle text-center">{term2 !== "" ? term2 : <span className="text-muted">—</span>}</td>
                      <td className="align-middle text-center fw-semibold">{sumValue}</td>
                      <td className="align-middle text-center">
                        <Button
                          size="sm"
                          variant={hasScore ? "outline-primary" : "outline-secondary"}
                          onClick={() => handleOpenScoreModal(s)}
                        >
                          <i className="ri-edit-line"></i>
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            </div>
          )}
        </Card.Body>
      </Card>
      <Modal show={showScoreModal} onHide={() => setShowScoreModal(false)} size="sm" centered>
        <Modal.Header closeButton>
          <Modal.Title className="fs-6">กรอกคะแนน</Modal.Title>
        </Modal.Header>
        {modalStudent && (
          <Modal.Body>
            <div className="text-center mb-3">
              <div className="fw-semibold fs-sm text-secondary mb-1">{modalStudent.st_idnumber}</div>
              <div className="arabic-text fs-5 fw-bold mb-1">
                {modalStudent.st_arabicname || <span className="text-muted fs-6">ไม่มีชื่ออาหรับ</span>}
              </div>
              <div className="fw-medium">{modalStudent.st_name}</div>
            </div>
            <hr className="my-2" />
            <Form.Group className="mb-3">
              <Form.Label className="fw-semibold">{t.scoreEntry.semester1} <span className="text-muted fw-normal">(0–50)</span></Form.Label>
              <Form.Control
                type="text"
                inputMode="decimal"
                className="text-center"
                value={modalTerm1}
                onChange={e => handleModalTerm1(e.target.value)}
                placeholder="—"
                autoFocus
              />
            </Form.Group>
            <Form.Group className="mb-1">
              <Form.Label className="fw-semibold">{t.scoreEntry.semester2} <span className="text-muted fw-normal">(0–50)</span></Form.Label>
              <Form.Control
                type="text"
                inputMode="decimal"
                className="text-center"
                value={modalTerm2}
                onChange={e => handleModalTerm2(e.target.value)}
                placeholder="—"
              />
            </Form.Group>
            {(modalTerm1 !== "" || modalTerm2 !== "") && (
              <div className="text-center mt-3 fs-sm text-secondary">
                รวม: <span className="fw-bold text-dark">
                  {[modalTerm1, modalTerm2].filter(v => v !== "").reduce((a, b) => a + Number(b), 0)}
                </span>
              </div>
            )}
          </Modal.Body>
        )}
        <Modal.Footer>
          <Button variant="secondary" size="sm" onClick={() => setShowScoreModal(false)} disabled={modalSaving}>ยกเลิก</Button>
          <Button variant="primary" size="sm" onClick={handleModalSave} disabled={modalSaving}>
            {modalSaving ? <><span className="spinner-border spinner-border-sm me-1"></span>กำลังบันทึก…</> : 'ยืนยัน'}
          </Button>
        </Modal.Footer>
      </Modal>
    </React.Fragment>
  );
}
